import * as SVG from 'svg.js';

import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener,
         Inject, OnInit, ViewChild, ViewRef } from '@angular/core';
import { MatIconRegistry } from '@angular/material';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';

import { TextPlaceholder
       } from 'collage-designer/stage/placeholder/text-placeholder';
import { StageService } from 'collage-designer/stage/stage.service';
import { Utils } from 'core/utils';
import { StageUtils } from 'core/utils.stage';
import { ToolboxComponent } from '../toolbox.component';
import { ToolboxService } from '../toolbox.service';

interface AnimationFrame
{
  transformation?: string;
  opacity?: number;
  duration?: number;
  animated?: boolean;
  callback?: () => void; // tslint:disable-line
}

@Component({
  selector: 'text-toolbox',
  templateUrl: './text-toolbox.component.html',
  styleUrls: [ '../toolbox.component.scss', './text-toolbox.component.scss', ],
})
export class TextToolboxComponent
    extends ToolboxComponent implements AfterViewInit, OnInit
{
  private static readonly animationGraceTime: number = 80; /* ms */
  private static readonly perspectiveGraceTime: number = 20; /* ms */
  private static readonly animationFrameDuration: number = 1000; /* ms */

  private static readonly barrelRoll: Array<AnimationFrame> = [
    { transformation:
        'perspective(20cm) rotateX(0deg) rotateY(0deg) scale(1)',
      animated: false,
      duration: TextToolboxComponent.animationGraceTime,
    },
    { transformation:
        'perspective(20cm) rotateX(80deg) rotateY(0deg) scale(0.6)',
      duration: 1000,
    },
    { transformation:
        'perspective(20cm) rotateX(80deg) rotateY(0deg) scale(0.6)',
      animated: false,
      duration: TextToolboxComponent.animationGraceTime,
    },
    { transformation:
        'perspective(20cm) rotateX(80deg) rotateY(360deg) scale(0.6)',
      duration: 1000,
    },
    { transformation:
        'perspective(20cm) rotateX(80deg) rotateY(360deg) scale(0.6)',
      animated: false,
      duration: TextToolboxComponent.animationGraceTime,
    },
    { transformation:
        'perspective(20cm) rotateX(0deg) rotateY(360deg) scale(1)',
      duration: 1000,
    },
    { transformation:
        'perspective(20cm) rotateX(0deg) rotateY(0deg) scale(1)',
      animated: false,
      duration: TextToolboxComponent.animationGraceTime,
    },
  ];

  public placeholder: TextPlaceholder;
  public heightOverride: string = '';
  public hostElementTransformation: SafeStyle;
  public hostElementOpacity: number = 1;
  public hostElementTransition: string = 'none';

  @ViewChild('editor') private editor: ElementRef;

  constructor(
      mir: MatIconRegistry,
      sanitizer: DomSanitizer,
      private cdr: ChangeDetectorRef,
      @Inject(ToolboxService) toolboxService,
      @Inject(StageService) private stageService: StageService)
  {
    super(mir, sanitizer, toolboxService);
    this.placeholder = <TextPlaceholder> this.placeholder;
  }

  public editingResponse(response: string): void
  {
    switch (response) {
      case 'confirm':
        this.startClosingAnimation(() => {
          this.btnOKClick();
          StageUtils.removeUnusedFonts(
              this.stageService.getCurrentStageReference());
        });
        break;
      case 'barrellRoll':
        this.animate([...TextToolboxComponent.barrelRoll]);
        break;
      default:
        this.startClosingAnimation(() => this.btnCancelClick());
        break;
    }
  }

  @HostListener('window:resize', [])
  public ngOnInit(): void
  {
    if (! Utils.isSmallDevice()) {
      return;
    }

    // Steve Jobs be damned!
    this.heightOverride = `${window.innerHeight}px`;
  }

  public ngAfterViewInit(): void {
    this.startOpeningAnimationAt(this.placeholder.wrapper);
  }

  private startClosingAnimation(onFinished: () => void): void
  {
    this.animate([
      { transformation:
          'perspective(0cm) rotateX(0deg) rotateY(0deg) scale(1)',
        opacity: 1,
        animated: false,
        duration: TextToolboxComponent.animationGraceTime,
      },
      { transformation:
        'perspective(20cm) rotateX(0deg) rotateY(0deg) scale(1)',
        opacity: 1,
        animated: false,
        duration: TextToolboxComponent.perspectiveGraceTime,
      },
      { transformation:
          'perspective(20cm) rotateX(10deg) rotateY(0deg) scale(0.85)',
        opacity: 0,
        duration: 450,
      },
      {
        callback: onFinished,
      }
    ]);
  }

  private setTransformation(transformation: string): void
  {
    this.hostElementTransformation =
      this.sanitizer.bypassSecurityTrustStyle(transformation);
  }

  private setOpacity(opacity: number): void
  {
    this.hostElementOpacity = opacity;
  }

  private setTransition(duration: number | undefined): void
  {
    this.hostElementTransition =
      duration !== undefined ? `${duration / 1000}s` : 'none';
  }

  private startOpeningAnimationAt(wrapper: SVG.Doc): void
  {
    if (Utils.isSmallDevice()) {
      this.startFadeInEffect();
    } else {
      this.startWhoooooooshEffect(wrapper);
    }
  }

  private startFadeInEffect(): void
  {
    this.animate([
      { transformation:
          'perspective(20cm) rotateX(10deg) rotateY(0deg) scale(0.85)',
        opacity: 0,
        animated: false,
        duration: TextToolboxComponent.animationGraceTime,
      },
      { transformation:
          'perspective(0cm) rotateX(0deg) rotateY(0deg) scale(1)',
        opacity: 1,
        duration: 450,
      },
    ]);
  }

  private startWhoooooooshEffect(wrapper: SVG.Doc): void
  {
    const frame = (<any>wrapper).parent() as SVG.G;

    const rotation = frame.transform().rotation || 0;
    const rotationMatrix = (new SVG.Matrix).rotate(-rotation);

    const source = this.editor.nativeElement.getBoundingClientRect();
    const target = wrapper.rbox();
    target.x -= window.pageXOffset;
    target.y -= window.pageYOffset;

    const mid = (coord: number, dimension: number) => coord + dimension / 2;
    const offsetX =
      mid(target.x, target.width) - mid(source.left, source.width);
    const offsetY =
      mid(target.y, target.height) - mid(source.top, source.height);

    const rotatedTarget = target.transform(rotationMatrix);
    const scaleX = rotatedTarget.width / source.width;
    const scaleY = rotatedTarget.width / source.width;
    const scale = Math.max(scaleX, scaleY);

    const normalizedRotation =
      Math.abs(rotation) <= 180 ? rotation : -(rotation % 180);
    this.animate([
      { transformation:
          `translate(${offsetX}px, ${offsetY}px) ` +
          `scale(${scale}) ` +
          `rotate(${normalizedRotation}deg) `,
        opacity: 0,
        animated: false,
        duration: TextToolboxComponent.animationGraceTime,
      },

      { transformation:
          `translate(0px, 0px) ` +
          `scale(1) ` +
          `rotate(0deg)`,
        opacity: 1,
      }
    ]);
  }

  private animate(steps: Array<AnimationFrame>): void
  {
    const next = steps.shift();

    if (! next) {
      this.setTransition(undefined);
      return;
    }

    const transformation = next.transformation;
    const opacity = next.opacity;
    const duration =
      next.duration || TextToolboxComponent.animationFrameDuration;
    const animated = next.animated === undefined ? true : next.animated;
    const callback = next.callback;

    this.setTransition(animated ? duration : undefined);

    if (transformation !== undefined) {
      this.setTransformation(transformation);
    }

    if (opacity !== undefined) {
      this.setOpacity(opacity);
    }

    if (callback !== undefined) {
      callback();
    }

    if (!(this.cdr as ViewRef).destroyed) {
      this.cdr.detectChanges();
    }

    setTimeout(() => this.animate(steps), duration);
  }
}



// WEBPACK FOOTER //
// ./src/app/toolboxes/text-toolbox/text-toolbox.component.ts